// Edges between grounds on the custom overworld chart (docs/70 CO9, docs/132 OP37). Where two kinds of
// ground meet, the one of higher rank frays a few pixels into its neighbour along a ragged edge, so a
// field of scree, a snowfield or a patch of heath doesn't end in a straight cut along the cell's side.
// Water against land has its banks instead (the shore pieces); shallow water frays into deep.
//
// The edges are masks, not art: the chart fills a mask with the neighbouring cell's own ground tile
// (its variant, its season, its time of day), so every folder gets them with no files of its own. The
// shapes below are the whole of it; change the numbers to change every edge. `tools/overworld-art.py`
// reads this file for its previews.
//
// Pure: no browser, so the tests and the seed script can read it.
import { cellSalt, type CustomChart } from './customChart';
import type { Biome } from './overworldBiomes';

/** How far a side's edge reaches into the cell at each of its 24 pixels, along the side (west to east
 *  for `n` and `s`, north to south for `e` and `w`). Every profile is 3 at both ends, so an edge always
 *  meets the next cell's edge and the corner nub. Four, so a long edge doesn't repeat. */
export const EDGE_DEPTHS: readonly (readonly number[])[] = [
  [3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 3, 2, 2, 3, 4, 4, 6, 5, 4, 5, 5, 4, 3, 3],
  [3, 3, 4, 4, 4, 5, 4, 5, 5, 7, 6, 6, 7, 6, 5, 5, 5, 5, 4, 4, 4, 3, 3, 3],
  [3, 4, 3, 2, 3, 3, 2, 2, 3, 4, 4, 6, 5, 5, 5, 5, 5, 6, 4, 4, 4, 4, 3, 3],
  [3, 3, 4, 4, 5, 6, 6, 7, 5, 6, 5, 5, 4, 4, 4, 4, 3, 3, 2, 3, 2, 3, 3, 3],
];
/** The outer corner's nub, row by row from the corner: how many pixels of each row it covers. Its first
 *  row and first column are 3, as the ends of the side edges it meets are. */
export const CORNER_NUB: readonly number[] = [3, 3, 2];
/** Edge depth at a side's ends: what every profile and the nub agree on. */
export const EDGE_END = 3;

export const EDGE_SIDES = ['n', 'e', 's', 'w'] as const;
export const EDGE_CORNERS = ['ne', 'se', 'sw', 'nw'] as const;
export type EdgePiece = (typeof EDGE_SIDES)[number] | (typeof EDGE_CORNERS)[number];

/**
 * Which ground frays into which: a ground spills into a neighbour of lower rank. Water and land are
 * kept apart (the banks join them). Farmland has none: its hedge, dyke or bund is its edge.
 */
export const GROUND_RANK: Readonly<Record<string, number>> = {
  deep_water: 0, water: 1,
  // the abyss ranks lowest of the land: every ground's edge hangs over its rim (docs/23 Y9)
  abyss: 9,
  plains: 10, meadow: 11, heath: 12, marsh: 13, sand: 14, barren: 15, snowfield: 16,
};
const WET = new Set(['water', 'deep_water']);

/**
 * docs/185 PB13: across a border between two regions the same ground is two grounds (one folder's plains against
 * another's), so it frays too. The higher `GROUND_RANK` still frays into the lower; between equal ranks (plains
 * against plains, water against water) the harsher, drier country frays into the softer, greener one, so the
 * desert's sand blows over the grass and the Ashlands' grit over everything. Water and land stay apart across a
 * border as within one (the banks join them), and a river never meets the Ashlands' lava (a crag stands between).
 */
export const BIOME_EDGE_RANK: Readonly<Record<Biome, number>> = {
  wetland: 1, jungle: 2, temperate: 3, underdark: 4, boreal: 5, highland: 6, desert: 7, ashlands: 8,
};
const biomeRank = (b: Biome | undefined) => (b ? BIOME_EDGE_RANK[b] : 0);

const CELL = 24;
const SIDE_STEP: Record<EdgePiece, readonly [number, number]> = {
  n: [0, -1], e: [1, 0], s: [0, 1], w: [-1, 0], ne: [1, -1], se: [1, 1], sw: [-1, 1], nw: [-1, -1],
};
const EDGE_SALT: Record<EdgePiece, number> = { n: 21, e: 22, s: 23, w: 24, ne: 25, se: 26, sw: 27, nw: 28 };

/** The mask of one edge piece: 24×24, 1 where the neighbour's ground shows. */
export function edgeMask(piece: EdgePiece, variant: number): Uint8Array {
  const m = new Uint8Array(CELL * CELL);
  const set = (x: number, y: number) => { m[y * CELL + x] = 1; };
  if (piece.length === 1) {
    const depth = EDGE_DEPTHS[variant % EDGE_DEPTHS.length]!;
    for (let i = 0; i < CELL; i++) for (let k = 0; k < depth[i]!; k++) {
      if (piece === 'n') set(i, k);
      else if (piece === 's') set(i, CELL - 1 - k);
      else if (piece === 'w') set(k, i);
      else set(CELL - 1 - k, i);
    }
  } else {
    CORNER_NUB.forEach((n, y) => {
      for (let x = 0; x < n; x++) set(piece.includes('w') ? x : CELL - 1 - x, piece.startsWith('n') ? y : CELL - 1 - y);
    });
  }
  return m;
}

export interface GroundEdge {
  piece: EdgePiece;
  /** Which of the piece's shapes (`EDGE_DEPTHS`), picked by the cell's hash. */
  variant: number;
  /** The neighbouring cell whose ground fills the edge. */
  from: readonly [number, number];
}

/** The edges laid on cell (x, y), lowest rank first, so a higher ground's edge lies on top. */
export function edgesAt(chart: CustomChart, x: number, y: number): GroundEdge[] {
  const { w, h, cells, seed } = chart;
  const cellAt = (gx: number, gy: number) => (gx >= 0 && gy >= 0 && gx < w && gy < h ? cells[gy * w + gx]! : undefined);
  const here = cellAt(x, y)!, me = here.ground.tile, mb = here.biome;
  const mine = GROUND_RANK[me];
  if (mine === undefined) return [];
  // a ground's place in the order: its rank, then its country's (PB13; a one-country chart has none)
  const rankOf = (t: string, b: Biome | undefined) => GROUND_RANK[t]! * 16 + biomeRank(b);
  const myRank = rankOf(me, mb);
  const over = (t: string | undefined, b: Biome | undefined): t is string =>
    t !== undefined && (t !== me || b !== mb) && GROUND_RANK[t] !== undefined && rankOf(t, b) > myRank && WET.has(t) === WET.has(me);
  const same = (gx: number, gy: number, t: string, b: Biome | undefined) => { const c = cellAt(gx, gy); return !!c && c.ground.tile === t && c.biome === b; };
  const out: Array<GroundEdge & { rank: number }> = [];
  for (const piece of [...EDGE_SIDES, ...EDGE_CORNERS]) {
    const [dx, dy] = SIDE_STEP[piece];
    const c = cellAt(x + dx, y + dy), t = c?.ground.tile;
    if (!over(t, c?.biome)) continue;
    // a corner nub only where neither side beside it already has that ground's edge
    if (piece.length === 2 && (same(x + dx, y, t, c?.biome) || same(x, y + dy, t, c?.biome))) continue;
    out.push({ piece, variant: cellSalt(x, y, seed, EDGE_SALT[piece]) % EDGE_DEPTHS.length, from: [x + dx, y + dy], rank: rankOf(t, c?.biome) });
  }
  return out.sort((a, b) => a.rank - b.rank).map(({ rank: _rank, ...e }) => e);
}
